import type { AssistantToolCall } from 'components/framework/models';
import type Agent from '../agents/Agent';

/**
 * Tool handler interface that defines how tools process tool calls.
 * Each tool handler is a simple object with methods for handling specific tools.
 */
export interface ToolHandler {
  /**
   * Give the Tool an opportunity to modify the data provided to a tool call
   */
  beforeApproveToolCall?(
    toolCall: AssistantToolCall,
    agent: Agent,
    data?: Record<string, unknown>
  ): Record<string, unknown> | undefined;

  /**
   * Give the Tool an opportunity to react to a tool call when it is approved
   * or rejected. Return false to prevent the action.
   */
  approveToolCall?(
    toolCall: AssistantToolCall,
    agent: Agent,
    data?: Record<string, unknown>
  ): boolean;

  rejectToolCall?(toolCall: AssistantToolCall, agent: Agent): boolean;

  /**
   * Give the Tool a way to provide additional data for a tool call.
   * Return false to skip providing data. Otherwise return an object with the
   * data to be provided.
   */
  provideToolCallData?(
    toolCall: AssistantToolCall,
    agent: Agent
  ): Object | false;

  /**
   * Give the Tool an opportunity to react to a tool call when it is created.
   */
  processToolCall?(toolCall: AssistantToolCall, agent: Agent): void;

  /**
   * Allows a Tool to provide an action that will be called when the content of the toolcall is clicked.
   */
  onContentAction?(
    toolCall: AssistantToolCall,
    agent: Agent
  ): (() => void) | undefined;

  /**
   * Allow a tool to mark itself as unavailable. By default, all tools are assumed available.
   */
  unavailable?(agent: Agent): boolean;

  // Display additional actions for the tool call.
  getToolActions?(
    toolCall: AssistantToolCall,
    agent: Agent
  ): React.ReactNode | null;
}

// The registry of all tool handlers. This is used to look up handlers by tool name.
const handlerRegistry: Record<string, ToolHandler> = {};

// Register a tool handler with the system so that it can be looked up by name.
export function register(toolName: string, handler: ToolHandler) {
  handlerRegistry[toolName] = handler;
}

// Look up a handler by tool name.
export function lookup(toolName: string): ToolHandler | undefined {
  return handlerRegistry[toolName];
}

// Convenience methods for common tool operations
export function processToolCall(
  toolCall: AssistantToolCall,
  agent: Agent
): void {
  const handler = lookup(toolCall.tool);
  handler?.processToolCall?.(toolCall, agent);
}

export function provideToolCallData(
  toolCall: AssistantToolCall,
  agent: Agent
): Object | false {
  const handler = lookup(toolCall.tool);
  return handler?.provideToolCallData?.(toolCall, agent) || false;
}

export function beforeApproveToolCall(
  toolCall: AssistantToolCall,
  agent: Agent,
  data?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const handler = lookup(toolCall.tool);
  return handler?.beforeApproveToolCall?.(toolCall, agent, data);
}

export function approveToolCall(
  toolCall: AssistantToolCall,
  agent: Agent,
  data?: Record<string, unknown>
): boolean {
  const handler = lookup(toolCall.tool);
  return handler?.approveToolCall?.(toolCall, agent, data) ?? true;
}

export function rejectToolCall(
  toolCall: AssistantToolCall,
  agent: Agent
): boolean {
  const handler = lookup(toolCall.tool);
  return handler?.rejectToolCall?.(toolCall, agent) ?? true;
}

export function onContentAction(
  toolCall: AssistantToolCall,
  agent: Agent
): (() => void) | undefined {
  const handler = lookup(toolCall.tool);
  return handler?.onContentAction?.(toolCall, agent);
}

export function getToolActions(
  toolCall: AssistantToolCall,
  agent: Agent
): React.ReactNode | null {
  const handler = lookup(toolCall.tool);
  return handler?.getToolActions?.(toolCall, agent) || null;
}

export function unavailableTools(agent?: Agent): string[] {
  if (!agent) return [];

  return Object.entries(handlerRegistry)
    .filter(([toolName, handler]) => handler.unavailable?.(agent) ?? false)
    .map(([toolName, _]) => toolName);
}

const Tool = {
  register,
  lookup,
  processToolCall,
  provideToolCallData,
  beforeApproveToolCall,
  approveToolCall,
  rejectToolCall,
  onContentAction,
  getToolActions,
  unavailableTools,
};

export default Tool;
